import React, { useCallback, useEffect, useMemo } from 'react';
import get from 'lodash/get';
import { useDispatch, useSelector } from 'react-redux';
import responseSelector, {
  dimensionTableColumnsSelector,
} from '@store/overviewDashboard/dimensionData/selectors';
import { initDimension, fetchData } from '@store/overviewDashboard/dimensionData/actions';
import Table from '@components/common/Table';
import { useTableSort } from '@components/common/Table/hooks';
import { kpiMetricSelector } from '@store/overviewDashboard/base/selectors';
import { benchmarkStore } from '@store/overviewDashboard/benchmark/selectors';
import { Retry } from '@components/common';
import TableSkeleton from '@components/overview-dashboard/common/TableSkeleton';
import { TableWrapper } from './styled';
import { formatColumns, formatData } from './utils';
import { useInitBenchmark, useIsDimensionSupportedAndFilterApplied } from './hooks';

function DimensionTable({ dimension, unit }) {
  const dispatch = useDispatch();
  const columns = useSelector(dimensionTableColumnsSelector);
  const selectedKpiMetric = useSelector(kpiMetricSelector);
  const dimensionsStore = useSelector(responseSelector);
  const dimensionData = get(dimensionsStore, [dimension], {});
  const benchmarkData = get(useSelector(benchmarkStore), dimension, {});

  const isBenchmarkSupportedAndFilterApplied = useIsDimensionSupportedAndFilterApplied(
    dimension,
    selectedKpiMetric,
  );
  useInitBenchmark(dimension, isBenchmarkSupportedAndFilterApplied);

  useEffect(() => {
    dispatch(initDimension(dimension));
  }, [dimension]);

  const formattedColumns = useMemo(
    () =>
      formatColumns({
        dimension,
        columns,
        unit,
        benchmarkData,
        selectedKpiMetric,
        isBenchmarkSupportedAndFilterApplied,
      }),
    [
      dimension,
      columns,
      unit,
      benchmarkData,
      selectedKpiMetric,
      isBenchmarkSupportedAndFilterApplied,
    ],
  );

  const { data, isFetching, isError, isEmpty } = useMemo(
    () => formatData(dimensionData, benchmarkData, dimension, columns, unit, selectedKpiMetric),
    [dimensionData, benchmarkData, dimension, columns, unit, selectedKpiMetric],
  );

  const retryFetchData = useCallback(() => {
    dispatch(fetchData(dimension));
  }, [dimension]);

  const { sortBy, onColumnSort, sortedData } = useTableSort({
    data,
    defaultSort: { key: columns[0], order: 'desc' },
  });

  return (
    <TableWrapper style={{ height: 400 }}>
      <Retry
        fetchData={retryFetchData}
        isLoading={isFetching}
        showError={isError}
        LoadingLayer={TableSkeleton}
        isEmpty={isEmpty}
      >
        <Table
          data={sortedData}
          columns={formattedColumns}
          headerHeight={50}
          rowHeight={45}
          sortBy={sortBy}
          onColumnSort={onColumnSort}
        />
      </Retry>
    </TableWrapper>
  );
}

export default DimensionTable;
